import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import classNames from 'classnames';
import throttle from 'utils/throttle';
import Text from 'components/text/text';
import Video from 'components/video/video';
import messages from './messages';

class DataModuleVideo extends React.Component {
  constructor(props) {
    super(props);

    this.container = null;
    this.video = null;
    this.scrollPosition = (window.pageYOffset || document.documentElement.scrollTop)  - (document.documentElement.clientTop || 0);

    this.state = { 'active': false };

    this.setUpHandlers();
  }

  /**
   * Binds any handlers to the correct context
   *
   * @method  setUpHandlers
   */
  setUpHandlers() {
    this.onWindowScroll = this.handleWindowScroll.bind(this);
    this.throttleScrollListener = throttle(this.onWindowScroll, 250);
  }

  /**
   * React Lifecycle Hook
   * Invoked once, immediately after the initial rendering occurs.
   *
   * @method  componentDidMount
   */
  componentDidMount() {
    this.addListeners();

    this.handleWindowScroll();
  }

  /**
   * React Lifecycle Hook
   * Invoked immediately before a component is unmounted and destroyed.
   * Perform any necessary cleanup in this method
   *
   * @method  componentWillUnmount
   */
  componentWillUnmount() {
    this.removeListeners();
  }

  addListeners() {
    window.addEventListener('scroll', this.throttleScrollListener);
  }

  removeListeners() {
    window.removeEventListener('scroll', this.throttleScrollListener);
  }

  handleWindowScroll(event) {
    const newScrollPos = (window.pageYOffset || document.documentElement.scrollTop)  - (document.documentElement.clientTop || 0);
    const containerMiddle = this.getOffset(this.container).top + this.container.offsetHeight/3;
    const containerIsInView = newScrollPos + window.innerHeight > containerMiddle;
    const isDownScroll = newScrollPos > this.scrollPosition || newScrollPos === this.scrollPosition;

    if (isDownScroll && containerIsInView && !this.video.state.isEnded) {
      this.playVideo();
      this.fadeInInfo();
    }

    this.scrollPosition = newScrollPos;
  }

  getOffset(el) {
    let rect = el.getBoundingClientRect();

    return {
      'top': rect.top + document.body.scrollTop,
      'left': rect.left + document.body.scrollLeft
    }
  }

  playVideo() {
    this.video.play();
  }

  fadeInInfo() {
    this.setState({
      'active': true
    });
  }

  _renderDataSet(descriptor, value, unit, position) {
    const infoClasses = classNames(
      'videoInfo-info',
      {
        'videoInfo-info_active': this.state.active,
        [`videoInfo-info_${position}`]: position
      }
    );

    return (
      <div className={infoClasses}>
        <div className="set">
          <div className="set-hd">
            <Text
              className="mix-txt_uppercase"
              color="DefaultGray"
              tag="span"
              type="sm"
              weight="normal"
            >
              {descriptor}
            </Text>
          </div>
          <div className="set-bd set-bd_flush">
            <Text
              color="Lightest"
              tag="span"
              type="mdLg"
              weight="light"
            >
              {value}<span className="txt txt_md mix-txt_lowercase">{unit}</span>
            </Text>
          </div>
        </div>
      </div>
    )
  }

  _renderDataSets() {
    const { intl, data } = this.props;
    const thighLabel = intl.formatMessage(messages.thigh_label);
    const shinLabel = intl.formatMessage(messages.shin_label);
    const sitBoneLabel = intl.formatMessage(messages.sit_bone_label);
    const lengthLabel = intl.formatMessage(messages.length_label);
    const circumferenceLabel = intl.formatMessage(messages.circumference_label);
    const archHeightLabel = intl.formatMessage(messages.arch_height_label);
    const notCollectedLabel = intl.formatMessage(messages.not_collected_label);
    const millimeterSymbol = intl.formatMessage(messages.millimeter_symbol);

    let sets, archHeight;

    switch (data.type) {
      case 'bike':
        sets = (
          <div>
            {this._renderDataSet(thighLabel, data.average_thigh_length, millimeterSymbol, 'topCenterRight')}
            {this._renderDataSet(shinLabel, data.average_shin_length, millimeterSymbol, 'botCenterRight')}
          </div>
        );
        break;

      case 'shoe':
        archHeight = data.arch_height;
        sets = (
          <div>
            {this._renderDataSet(lengthLabel, data.foot_length, millimeterSymbol, 'botRight')}
            {this._renderDataSet(circumferenceLabel, data.ball_circumference, millimeterSymbol, 'botLeft')}
            {this._renderDataSet(archHeightLabel, archHeight || '', archHeight ? '' : notCollectedLabel, 'centerRight')}
          </div>
        );
        break;

      case 'saddle':
        sets = this._renderDataSet(sitBoneLabel, data.it_width, millimeterSymbol, 'topRight');
        break;

      default:
        sets = '';
    }

    return sets;
  }

  _renderImage() {
    return (
      <img
        src={require(`media/images/body-data/${this.props.data.type}.png`)}
        alt={`3D ${this.props.data.type} Render`}
        className="videoInfo-image"
      />
    )
  }

  render() {
    const dataSets = this.props.data.empty ? '' : this._renderDataSets();
    const image = this._renderImage();

    return (
      <div
        className="videoInfo"
        ref={container => { this.container = container; }}
      >
        {dataSets}
        {image}
        <Video
          className="videoInfo-video"
          ref={video => { this.video = video; }}
          src={require(`media/videos/video-${this.props.data.type}.mp4`)}
        />
      </div>
    )
  }
}

const bikeDataShape = PropTypes.shape({
  'average_shin_length': PropTypes.number,
  'average_thigh_length': PropTypes.number,
  'empty': PropTypes.bool,
  'type': PropTypes.oneOf(['bike']).isRequired
});

const saddleDataShape = PropTypes.shape({
  'empty': PropTypes.bool,
  'it_width': PropTypes.number,
  'type': PropTypes.oneOf(['saddle']).isRequired
});

const shoeDataShape = PropTypes.shape({
  'arch_height': PropTypes.number,
  'ball_circumference': PropTypes.number,
  'empty': PropTypes.bool,
  'foot_length': PropTypes.number,
  'type': PropTypes.oneOf(['shoe']).isRequired
});

DataModuleVideo.propTypes = {
  'data': PropTypes.oneOfType([bikeDataShape, saddleDataShape, shoeDataShape]).isRequired
};

export default injectIntl(DataModuleVideo);



// WEBPACK FOOTER //
// ./src/client/pages/body-data/_data-module-video.jsx